import { CategoryFacetViewModel } from '@/types/api'

function setCategoryKeyValueRecursively(
    item: CategoryFacetViewModel,
    targetKey: string,
    newValue: CategoryFacetViewModel
): void {
    if (item.key === targetKey) {
        item = newValue
        return
    }

    if (item.children) {
        for (const child of item.children) {
            setCategoryKeyValueRecursively(child, targetKey, newValue)
        }
    }
}

export function setCategoryKeyValueRecursivelyInList(
    items: CategoryFacetViewModel[],
    targetKey: string,
    newValue: CategoryFacetViewModel
): void {
    for (const item of items) {
        setCategoryKeyValueRecursively(item, targetKey, newValue)
    }
}

// find a category and change current state
// toggle isSelected value - just one category
export function updateSelectedStatus(
    parent: CategoryFacetViewModel[],
    child: CategoryFacetViewModel,
    isSelected: boolean
): void {
    for (const item of parent) {
        if (item.key === child.key) {
            item.isSelected = isSelected

            return // Exit the loop after updating the target item
        }

        if (item.children?.length) {
            updateSelectedStatus(item.children, child, isSelected) // Recursively update in children
        }
    }
}

// check categories
// (all child selected -> parent should be selected)
// (all child un-selected -> parent should be un-selected)
export function checkCategoriesStatusRecursively(items: CategoryFacetViewModel[]): void {
    for (const item of items) {
        if (item.children?.length) {
            let allSelected = true
            let allUnselected = true

            for (const child of item.children) {
                checkCategoriesStatusRecursively(item.children)

                if (child.isSelected) {
                    allUnselected = false
                } else {
                    allSelected = false
                }
            }

            if (allSelected) {
                item.isSelected = true
            } else if (allUnselected) {
                item.isSelected = false
            }
        }
    }
}

// traverse an array of CategoryFacetViewModel items and return a flat array of isSelected=true ones
export function getSelectedCategoryItems(categoryFacets: CategoryFacetViewModel[]): CategoryFacetViewModel[] {
    return categoryFacets.reduce((selectedItems, item) => {
        if (item.isSelected) {
            selectedItems.push(item)
        }

        if (item.children) {
            selectedItems.push(...getSelectedCategoryItems(item.children))
        }

        return selectedItems
    }, [] as CategoryFacetViewModel[])
}

export function setCategoriesSelectedProp(
    categories: CategoryFacetViewModel[],
    isSelected: boolean
): CategoryFacetViewModel[] {
    return categories.map((category) => {
        const updatedCategory: CategoryFacetViewModel = {
            ...category,
            isSelected,
        }

        if (category.children && category.children.length > 0) {
            updatedCategory.children = setCategoriesSelectedProp(category.children, isSelected)
        }

        return updatedCategory
    })
}

// receives a single category or array of categories and finds the one with the given key/value
export function findCategoryByKeyValue(
    data: CategoryFacetViewModel | CategoryFacetViewModel[],
    key: string,
    value: string | number | boolean | null
): CategoryFacetViewModel | null {
    if (Array.isArray(data)) {
        for (const item of data) {
            const found = findCategoryByKeyValue(item, key, value)
            if (found) {
                return found
            }
        }
        return null
    } else {
        if (data[key as keyof CategoryFacetViewModel] === value) {
            return data
        }
        if (data.children) {
            const childFound = findCategoryByKeyValue(data.children, key, value)
            if (childFound) {
                return childFound
            }
        }
        return null
    }
}

// if user is in category page, get current category to be passed to Facets and to show childrens' links
export const getNextLevelCategoies = (currentLevelCategory?: CategoryFacetViewModel[], displayNameEn?: string) => {
    return !!currentLevelCategory && !!displayNameEn
        ? findCategoryByKeyValue(currentLevelCategory, 'displayNameEn', displayNameEn)?.children
        : null
}
